/**
 * Tenant Config Service
 * Fetches tenant-specific configuration overrides and merges with Remote Config defaults
 */
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { TenantConfig, EffectiveConfig } from '../models/config.types';
import { ConfigService } from './config.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class TenantConfigService {
  private apiService = inject(ApiService);
  private configService = inject(ConfigService);
  private baseUrl: string = '';

  private tenantConfigSubject = new BehaviorSubject<TenantConfig | null>(null);
  public tenantConfig$: Observable<TenantConfig | null> = this.tenantConfigSubject.asObservable();

  /**
   * Set base URL for API calls
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get tenant config from API
   */
  getTenantConfig(tenantId: string): Observable<TenantConfig> {
    return from(
      this.apiService.get<TenantConfig>(this.baseUrl, `/config_api/api/tenants/${tenantId}/config`),
    ).pipe(
      switchMap((obs) => obs),
      tap((config) => {
        this.tenantConfigSubject.next(config);
        // Update effective config in ConfigService
        const defaults = {
          pagination_default_limit: this.configService.getPaginationLimit(),
          pagination_max_limit: this.configService.getMaxPaginationLimit(),
          api_base_url: this.configService.getApiUrl(),
          form_listener_timeout_hours: 1, // Default 1 hour, from Remote Config
          feature_flags: {},
        };
        this.configService.updateEffectiveConfig(defaults, config);
      }),
      catchError((error) => {
        console.error('Failed to fetch tenant config:', error);
        // Return empty config on error
        const emptyConfig: TenantConfig = {};
        this.tenantConfigSubject.next(emptyConfig);
        return of(emptyConfig);
      }),
    );
  }

  /**
   * Update tenant config via API
   */
  updateTenantConfig(tenantId: string, config: Partial<TenantConfig>): Observable<TenantConfig> {
    return from(
      this.apiService.put<TenantConfig, Partial<TenantConfig>>(
        this.baseUrl,
        `/config_api/api/tenants/${tenantId}/config`,
        config,
      ),
    ).pipe(
      switchMap((obs) => obs),
      tap((updatedConfig) => {
        this.tenantConfigSubject.next(updatedConfig);
        // Update effective config in ConfigService
        const effectiveConfig = this.configService.getEffectiveConfig();
        const defaults = {
          pagination_default_limit: this.configService.getPaginationLimit(),
          pagination_max_limit: this.configService.getMaxPaginationLimit(),
          api_base_url: this.configService.getApiUrl(),
          form_listener_timeout_hours: effectiveConfig?.formListenerTimeoutHours ?? 1,
          feature_flags: {},
        };
        this.configService.updateEffectiveConfig(defaults, updatedConfig);
      }),
      catchError((error) => {
        console.error('Failed to update tenant config:', error);
        throw error;
      }),
    );
  }

  /**
   * Get effective config (merged Remote Config + tenant overrides)
   */
  getEffectiveConfig(): Observable<EffectiveConfig | null> {
    return this.configService.effectiveConfig$;
  }

  /**
   * Get current tenant config
   */
  getCurrentTenantConfig(): TenantConfig | null {
    return this.tenantConfigSubject.value;
  }
}
